import assert from '@/sui/tools/assert'
import microOrgs from '@/sui/data/microOrgs'

function getValue (v, c, n, str) {
  if (v == null || v === '') {
    return null
  }

  const value = str ? v : parseInt(v)
  const result = c.find(o => o.f.indexOf(value) !== -1)

  if (result != null) {
    if (result.s) {
      return result.s === 'null' ? null : result.s
    }
    return value
  }

  throw new Error(`Invalid field ${n}: ${v}`)
}

function getOrigin (v) {
  return getValue(
    v,
    [
      { f: [1], s: 1 }, { f: [2], s: 2 }, { f: [3], s: 4 }, { f: [4, 5, 9], s: 6 }
    ],
    'origin'
  )
}

function getDead (v) {
  return getValue(
    v,
    [
      { f: [1, 2, 3], s: 2 }, { f: [4], s: 1 }
    ],
    'dead'
  )
}

function getFuture (v) {
  return getValue(
    v,
    [
      { f: [1], s: 'DOM' }, { f: [2], s: 'ETAB' }, { f: [3], s: 'AUTETAB' },
      { f: [9], s: 'NC' }
    ],
    'future'
  )
}

function preparePatient (p, t) {
  t.sheetNumber = p.codenn
  t.sex = p.sexe
  t.entry = p.datentree
  t.birthSite = p.lieunais
  t.birthWeight = p.poids
  t.gestationalAge = p.AG
  t.leucomalacy = p.leuco
  t.origin = getOrigin(p.provenance)
  t.cribII = 999
  t.otherScoreName = null
  t.otherScoreValue = null
  t.dead = getDead(p.modesortie)
  t.covidStatus = 9
  if (t.dead === 1) {
    t.death = p.datsortie
    t.exit = null
    t.future = null
  } else {
    t.exit = p.datsortie
    t.death = null
    t.future = getFuture(p.modesortie)
  }
  t.birthdate = p.ddn
}

function setValues (t, prefix, values) {
  values.forEach(
    v => { t[`${prefix}${v.name}`] = v.value }
  )
}

function getType (v) {
  return getValue(
    v,
    [ { f: [1], s: 'C3' }, { f: [2, 3, 4, 5, 6, 7, 8], s: 'C1' } ],
    'catheter type'
  )
}

function getMaterial (v) {
  if (v == null || v === '') {
    return null
  }

  return getValue(
    v,
    [ { f: [1, 2, 3] }, { f: [9], s: 4 }, { f: [0], s: 9 } ],
    'catheter material'
  )
}

function getInsertionSite (v) {
  return getValue(
    v,
    [
      { f: [1, 8], s: 1 }, { f: [2], s: 7 }, { f: [3] }, { f: [4], s: 2 },
      { f: [5], s: 8 }, { f: [6], s: 10 }, { f: [7], s: 4 }
    ],
    'catheter insertion site'
  )
}

function getRemovalReason (v) {
  return getValue(
    v,
    [
      { f: [1, 2, 3, 4, 5] }, { f: [9], s: 6 }, { f: [0], s: 9 }
    ],
    'catheter removal reason'
  )
}

function getIsColIlcBlcSep (p) {
  if (p.bacterie === 1 && p.autporte === 2) {
    return 4
  }

  if (p.sepsis === 1 && p.sepautpo === 2) {
    return 5
  }

  return 0
}

function getColIlcBlcSep (p, col) {
  if (col === 4) {
    return p.datehemo
  }
  return null
}

function getColIlcBlcSepDiag (p, col) {
  if (col === 5) {
    return p.datsepsis
  }
  return null
}

function getLipidsDiagnosis (p, col) {
  if (col === 4) {
    return getValue(
      p.lipdiag, [{ f: [1, 2] }, { f: [0], s: 9 }], 'catheter lipids diagnosis'
    )
  }
  if (col === 5) {
    return getValue(
      p.lipsep, [{ f: [1, 2] }, { f: [0], s: 9 }], 'catheter lipids diagnosis (sep)'
    )
  }

  if (col === 0) {
    return 9
  }

  return null
}

function getBlcCriteria (p, col) {
  if (col !== 4) {
    return null
  }
  return getValue(
    p.critere, [{ f: [1, 2, 3, 4, 5] }], 'catheter blc criteria'
  )
}

function clean (p) {
  if (p.bacterie === 1 && p.autporte === 0) {
    p.autporte = p.portentr === 0 ? 2 : 1
  }
  if (p.sepsis === 1 && p.sepautpo === 0) {
    p.sepautpo = p.posepsis === 0 ? 2 : 1
  }
}

function prepareCatheter (p, t) {
  assert.notNull(t.ktIndex, 'invalid index')
  const col = getIsColIlcBlcSep(p)
  const labo = p.cultcvc !== 0 ? getValue(
    p.cultcvc, [{ f: [ 1, 2 ] }], 'laboratory'
  ) : 9
  setValues(
    t,
    `kt${t.ktIndex}`,
    [
      { name: 'Type', value: getType(p.siege) },
      { name: 'Material', value: getMaterial(p.materiau) },
      { name: 'Insertion', value: p.datepose },
      { name: 'InsertionSite', value: getInsertionSite(p.siege) },
      {
        name: 'Ventilation',
        value: getValue(p.ventass, [{ f: [1, 2, 3] }, { f: [0], s: 9 }], 'catheter ventilation')
      },
      {
        name: 'Lipids',
        value: getValue(
          p.nutlip, [{ f: [1, 2] }, { f: [0], s: 9 }], 'catheter lipids'
        )
      },
      {
        name: 'Removed',
        value: getValue(p.retrait, [{ f: [1, 2] }], 'catheter removed')
      },
      { name: 'Removal', value: p.dateretrait },
      { name: 'RemovalReason', value: getRemovalReason(p.raisonretrait) },
      { name: 'IsColIlcBlcSep', value: col },
      { name: 'ColIlcBlcSep', value: getColIlcBlcSep(p, col) },
      { name: 'ColIlcBlcSepOrigin', value: null },
      { name: 'DiagDate', value: getColIlcBlcSepDiag(p, col) },
      { name: 'LipidsDiagnosis', value: getLipidsDiagnosis(p, col) },
      { name: 'BlcCriteria', value: getBlcCriteria(p, col) },
      { name: 'DivDiag', value: getBlcCriteria(p, col) },
      { name: 'Covid', value: 9 },
      { name: 'ClinicalSigns', value: 9 },
      { name: 'BioSigns', value: 9 },
      { name: 'AdaptedAntibio', value: 9 },
      { name: 'TwoHemocs', value: 9 },
      { name: 'Laboratory', value: labo }
    ]
  )
  if (col === 4) {
    setValues(
      t,
      `kt${t.ktIndex}`,
      [
        { name: 'MO1', value: getMicroOrganism(p.morg1) },
        { name: 'Resist1', value: getResistance(p.morg1, p.res1) },
        { name: 'MO2', value: getMicroOrganism(p.morg2) },
        { name: 'Resist2', value: getResistance(p.morg2, p.res2) }
      ]
    )
  }
  t.ktIndex += 1
}

function getMicroOrganism (v) {
  if (v == null || v === '') {
    return null
  }

  return v
}

const allowedResistances = [
  {
    codes: ['STA AUR'],
    values: [
      { f: ['A'], s: 'OXA=S GLY=S PAN=N' },
      { f: ['B'], s: 'OXA=R GLY=S PAN=N' },
      { f: ['C'], s: 'OXA=NC GLY=R PAN=NC' },
      { f: ['Z'], s: 'OXA=NC GLY=NC PAN=NC' }
    ]
  },
  {
    codes: ['STA CAP'],
    values: [
      { f: ['A'], s: 'OXA=S GLY=S RKTG=NC' },
      { f: ['B'], s: 'OXA=R GLY=S RKTG=NC' },
      { f: ['C'], s: 'OXA=NC GLY=R RKTG=NC' },
      { f: ['Z'], s: 'OXA=NC GLY=NC RKTG=NC' }
    ]
  },
  {
    codes: ['ENC FAE', 'ENC FAI'],
    values: [
      { f: ['A', 'B'], s: 'GLY=S PAN=N' },
      { f: ['C'], s: 'GLY=R PAN=NC' },
      { f: ['Z'], s: 'GLY=NC PAN=NC' }
    ]
  },
  {
    codes: microOrgs.enterobacteria,
    values: [
      { f: ['A'], s: 'C3G=S BLSE=NC CAR=S PAN=N' },
      { f: ['B', 'C'], s: 'C3G=R BLSE=NC CAR=S PAN=N' },
      { f: ['D'], s: 'CSG=NC BLSE=NC CAR=R PAN=NC' },
      { f: ['Z'], s: 'C3G=NC BLSE=NC CAR=NC PAN=NC' }
    ]
  },
  {
    codes: ['ACI BAU'],
    values: [
      { f: ['A', 'B'], s: 'CAR=S PAN=N' },
      { f: ['C'], s: 'CAR=R PAN=NC' },
      { f: ['D', 'Z'], s: 'CAR=NC PAN=NC' }
    ]
  },
  {
    codes: ['PSE AER'],
    values: [
      { f: ['A', 'B'], s: 'CAR=S PAN=N' },
      { f: ['C', 'D'], s: 'CAR=R PAN=NC' },
      { f: ['Z'], s: 'CAR=NC PAN=NC' }
    ]
  },
  {
    codes: microOrgs.candida,
    values: [
      { f: ['A', 'B', 'C', 'D', 'Z'], s: 'FLU=NC' }
    ]
  }
]

function getResistance (mo, res) {
  if (mo == null || mo === '' || res == null || res === '') {
    return null
  }

  const c = `${mo.slice(0, 3)} ${mo.slice(3, 6)}`
  const m = microOrgs.list.v2.find((m) => m.code === c)
  if (m == null) {
    return null
  }

  const r = allowedResistances.find(r => r.codes.indexOf(c) !== -1)
  if (r == null) {
    return null
  }
  return getValue(res, r.values, `resistance ${c}`, true)
}

function prepareBacteremia (p, t) {
  if (p.bacterie !== 1 || p.autporte === 2) {
    return
  }

  setValues(
    t,
    `bact${t.bactIndex}`,
    [
      { name: 'DiagDate', value: p.datehemo },
      { name: 'Covid', value: 9 },
      { name: 'Entrance', value: getBacteremiaEntrance(p) },
      { name: 'MO1', value: getMicroOrganism(p.morg1) },
      { name: 'Resist1', value: getResistance(p.morg1, p.res1) },
      { name: 'MO2', value: getMicroOrganism(p.morg2) },
      { name: 'Resist2', value: getResistance(p.morg2, p.res2) }
    ]
  )
  t.bactIndex += 1
}

function getSepsisEntrance (p) {
  assert(
    p.sepsis === 1 && p.sepautpo === 1,
    `invalid sepsis data: ${p.sepsis}, ${p.sepautpo}`
  )
  if (p.posepsis === 0) {
    p.posepsis = 9
  }
  return getEntrance(p.posepsis, 'sepsis entrance')
}

function getBacteremiaEntrance (p) {
  assert(
    p.bacterie === 1 && p.autporte === 1,
    `invalid bact data: ${p.bacterie}, ${p.autporte}`
  )
  return getEntrance(p.portentr, 'bacteremia entrance')
}

function getEntrance (v, n) {
  return getValue(
    v,
    [
      { f: [1], s: 'A3' }, { f: [2], s: 'A1' }, { f: [3], s: 'A5' },
      { f: [4], s: 'A7' }, { f: [5], s: 'A3' }, { f: [9], s: 'A13' }
    ],
    n
  )
}

function prepareSepsis (p, t) {
  if (p.sepsis !== 1 || p.sepautpo === 2) {
    return
  }

  setValues(
    t,
    `sepsis${t.sepsisIndex}`,
    [
      { name: 'Episod', value: null },
      { name: 'DiagDate', value: p.datsepsis },
      { name: 'Origin', value: 9 },
      { name: 'Entrance', value: getSepsisEntrance(p) },
      { name: 'Covid', value: 9 },
      { name: 'Lipids', value: null }
    ]
  )
  t.sepsisIndex += 1
}

function prepare (p, t) {
  if (t.ktIndex == null) {
    preparePatient(p, t)
    t.ktIndex = 0
    t.bactIndex = 0
    t.sepsisIndex = 0
  }

  clean(p)
  prepareCatheter(p, t)
  prepareBacteremia(p, t)
  prepareSepsis(p, t)
}

export default {
  prepare: prepare
}
